import { IHttpService } from 'angular';
import { EventRegistry } from 'app/shared/events/event_registry';
import { SECONDS_IN_A_DAY, toUnix } from 'app/shared/utilities/time';
import {
    FetchFunction,
    formatDateToISO8601,
    getAheadSeconds,
    getDelta,
    requestUTCTime,
    showToast,
    ShowToastFunction,
    TimeSyncError,
} from 'app/shared/time/server_time_offset_utils';
import { ConfigService } from 'app/config/config.service';
import { StateMachine } from 'app/shared/state_machine/StateMachine';
import { Interval } from 'app/shared/utilities/interval';
import { HTMLDomDocumentService } from 'app/shared/html_dom_document_service';
import { PromisedTimeout } from 'app/shared/utilities/PromisedTimeout';
import DebugService from 'app/shared/debug.service';
import { Destructor } from 'app/shared/utilities/Destructor';

const STATE__DISABLED = 'disabled';
const STATE__IDLE = 'idle';
const STATE__INTERNET_TIME_REQUEST = 'internet_time_request';
const STATE__IMS_API_TIME_REQUEST = 'ims_api_time_request';

const TRANS__INTERNET_TIME_REQUEST_SUCCESS = 'internet_time_request_success';
const TRANS__IMS_API_TIME_REQUEST_SUCCESS = 'ims_api_time_request_success';
const TRANS__INTERNET_TIME_REQUEST_FAILED = 'internet_time_request_failed';
const TRANS__IMS_API_TIME_REQUEST_FAILED = 'ims_api_time_request_failed';
const TRANS__PAGE_VISIBLE = 'page_visible';
const TRANS__PAGE_HIDDEN = 'page_hidden';
const TRANS__RUN_INTERVAL = 'run_interval';
const TRANS__TIMER_TIMEOUT = 'timer_timeout';

const log = (...args) => DebugService.log('ServerTimeOffsetService', ...args);

interface TimeSyncCheckContext {
    // start the interval that triggers the time sync check process
    startInterval(): void;

    // stop the interval that triggers the time sync check process
    stopInterval(): void;

    // start the timer for the http requests
    startRequestTimer(): void;

    // start the request for the internet utc time
    startInternetTimeRequest(): void;

    // cancel the request for the internet utc time
    cancelInternetTimeRequest(): void;

    // callback to set the stored internet time
    acceptInternetTime(d: Date): void;

    // start the request for the api server time
    startIMSAPITimeRequest(): void;

    // cancel the request for the api server time
    cancelIMSAPITimeRequest(): void;

    // callback to set the stored API time
    acceptIMSAPITime(d: Date): void;

    // everything has worked - do the time comparison
    compareTimes(): void;

    // Allows the tests to wait until the state machine returns to the idle state, i.e.
    // zug zug, work complete!@
    enteringIdle(): void;
}

class TimeSyncCheckStateMachine {
    private readonly stateMachine: StateMachine<TimeSyncCheckContext>;

    // eslint-disable-next-line max-lines-per-function
    constructor(context: TimeSyncCheckContext) {
        // See https://miro.com/app/board/o9J_kpsaF6M=/?moveToWidget=3074457357595295219&cot=14
        this.stateMachine = new StateMachine(
            [
                {
                    name: STATE__DISABLED,
                },
                {
                    name: STATE__IDLE,
                    onAfterEnterActions: [
                        () => {
                            context.startInterval();
                            context.enteringIdle();
                        },
                    ],
                    onBeforeExitActions: [
                        () => {
                            context.stopInterval();
                            // Clear the stored times for the next requests...
                            context.acceptInternetTime(null);
                            context.acceptIMSAPITime(null);
                        },
                    ],
                },
                {
                    name: STATE__INTERNET_TIME_REQUEST,
                    onAfterEnterActions: [
                        () => {
                            context.startRequestTimer();
                            context.startInternetTimeRequest();
                        },
                    ],
                    onBeforeExitActions: [
                        () => {
                            context.cancelInternetTimeRequest();
                        },
                    ],
                },
                {
                    name: STATE__IMS_API_TIME_REQUEST,
                    onAfterEnterActions: [
                        () => {
                            context.startRequestTimer();
                            context.startIMSAPITimeRequest();
                        },
                    ],
                    onBeforeExitActions: [
                        () => {
                            context.cancelIMSAPITimeRequest();
                        },
                    ],
                },
            ],
            [
                // DISABLED
                {
                    eventName: TRANS__PAGE_VISIBLE,
                    sourceStateName: STATE__DISABLED,
                    destStateName: STATE__IDLE,
                },
                {
                    eventName: TRANS__INTERNET_TIME_REQUEST_FAILED,
                    sourceStateName: STATE__DISABLED,
                },
                {
                    eventName: TRANS__IMS_API_TIME_REQUEST_FAILED,
                    sourceStateName: STATE__DISABLED,
                },
                {
                    eventName: TRANS__INTERNET_TIME_REQUEST_SUCCESS,
                    sourceStateName: STATE__DISABLED,
                },
                {
                    eventName: TRANS__IMS_API_TIME_REQUEST_SUCCESS,
                    sourceStateName: STATE__DISABLED,
                },
                // Page hidden sends all states back to disabled!
                ...[
                    STATE__DISABLED,
                    STATE__IDLE,
                    STATE__INTERNET_TIME_REQUEST,
                    STATE__IMS_API_TIME_REQUEST,
                ].map((state) => {
                    return {
                        eventName: TRANS__PAGE_HIDDEN,
                        sourceStateName: state,
                        destStateName: STATE__DISABLED,
                    };
                }),
                // IDLE
                {
                    eventName: TRANS__RUN_INTERVAL,
                    sourceStateName: STATE__IDLE,
                    destStateName: STATE__INTERNET_TIME_REQUEST,
                },
                // IDLE - when most events happen in idle it's because things have happened slightly
                // out of order - just don't do anything!
                {
                    eventName: TRANS__PAGE_VISIBLE,
                    sourceStateName: STATE__IDLE,
                },
                {
                    eventName: TRANS__INTERNET_TIME_REQUEST_FAILED,
                    sourceStateName: STATE__IDLE,
                },
                {
                    eventName: TRANS__IMS_API_TIME_REQUEST_FAILED,
                    sourceStateName: STATE__IDLE,
                },
                {
                    eventName: TRANS__INTERNET_TIME_REQUEST_SUCCESS,
                    sourceStateName: STATE__IDLE,
                },
                {
                    eventName: TRANS__IMS_API_TIME_REQUEST_SUCCESS,
                    sourceStateName: STATE__IDLE,
                },
                // INTERNET_TIME_REQUEST - no matter what we progress to the
                // IMS API request state
                {
                    eventName: TRANS__PAGE_VISIBLE,
                    sourceStateName: STATE__INTERNET_TIME_REQUEST,
                },
                {
                    eventName: TRANS__TIMER_TIMEOUT,
                    sourceStateName: STATE__INTERNET_TIME_REQUEST,
                    destStateName: STATE__IMS_API_TIME_REQUEST,
                },
                {
                    eventName: TRANS__INTERNET_TIME_REQUEST_FAILED,
                    sourceStateName: STATE__INTERNET_TIME_REQUEST,
                    destStateName: STATE__IMS_API_TIME_REQUEST,
                },
                {
                    // Er, we've really gotten out of step here - reset to idle
                    eventName: TRANS__IMS_API_TIME_REQUEST_FAILED,
                    sourceStateName: STATE__IDLE,
                },
                {
                    // Er, we've really gotten out of step here - reset to idle
                    eventName: TRANS__IMS_API_TIME_REQUEST_SUCCESS,
                    sourceStateName: STATE__IDLE,
                },
                {
                    eventName: TRANS__INTERNET_TIME_REQUEST_SUCCESS,
                    sourceStateName: STATE__INTERNET_TIME_REQUEST,
                    destStateName: STATE__IMS_API_TIME_REQUEST,
                    beforeActions: [
                        (d) => {
                            context.acceptInternetTime(d);
                        },
                    ],
                },
                // API_TIME_REQUEST
                {
                    eventName: TRANS__PAGE_VISIBLE,
                    sourceStateName: STATE__IMS_API_TIME_REQUEST,
                },
                {
                    eventName: TRANS__TIMER_TIMEOUT,
                    sourceStateName: STATE__IMS_API_TIME_REQUEST,
                    destStateName: STATE__IDLE,
                },
                {
                    eventName: TRANS__IMS_API_TIME_REQUEST_FAILED,
                    sourceStateName: STATE__IMS_API_TIME_REQUEST,
                    destStateName: STATE__IDLE,
                },
                {
                    // We don't care if the internet request failed event
                    // comes in during the api request
                    eventName: TRANS__INTERNET_TIME_REQUEST_FAILED,
                    sourceStateName: STATE__IMS_API_TIME_REQUEST,
                },
                {
                    // We don't care if the internet request succeed event
                    // comes in during the api request - ignore the result
                    eventName: TRANS__INTERNET_TIME_REQUEST_FAILED,
                    sourceStateName: STATE__IMS_API_TIME_REQUEST,
                },
                {
                    eventName: TRANS__IMS_API_TIME_REQUEST_SUCCESS,
                    sourceStateName: STATE__IMS_API_TIME_REQUEST,
                    destStateName: STATE__IDLE,
                    beforeActions: [
                        (d) => {
                            context.acceptIMSAPITime(d);
                            context.compareTimes();
                        },
                    ],
                },
            ],
            {
                startingState: STATE__DISABLED,
                context: context,
                name: 'ServerTimeOffsetService',
            },
        );
    }

    public handleEvent(name: string, ...eventArgs): Promise<unknown[]> {
        return this.stateMachine.handleEvent(name, ...eventArgs);
    }

    /**
     * Danger - only to be used for testing
     */
    public forceState(stateName: string): void {
        this.stateMachine.forceState(stateName);
    }

    /**
     * Used for test assertions
     */
    public getCurrentState(): string {
        return this.stateMachine.currentStateName;
    }
}

/**
 * Retrieves times from the internet and the IMS API, compares these times with the browser's
 * time and pops a helpful toast if the time offsets are too large.
 */
export class ServerTimeOffsetService implements TimeSyncCheckContext {
    private _$http;
    private _browserTimeFactory: () => Date;
    private _fetch: FetchFunction;
    private _showToast: ShowToastFunction;
    private _lastTimeToastShown: Date | null;
    private _onChange: EventRegistry<[number, Date]>;
    private _destructor: Destructor;
    private _lastSecondsServerIsAheadOfClient: number | null;
    private _timeSyncThreshold: number;
    private _configService: ConfigService;
    private _enterIdleEvent: EventRegistry;
    private _serverTimestamp: Date;
    private _secondsServerIsAheadOfClient;

    // state machine driven members
    private _requestTimer: PromisedTimeout;
    private _promisedTimeoutFactory: (timeout: number) => PromisedTimeout;
    private readonly _timeSyncStateMachine: TimeSyncCheckStateMachine;
    private readonly _interval: Interval;
    private _abortController: AbortController;
    private readonly _htmlDomDocumentService: HTMLDomDocumentService;
    private _timeSyncCheckInternetTime: Date;
    private _timeSyncCheckIMSAPITimestamp: Date;
    private _cancelIMSAPIRequest: () => void;

    constructor(
        interval: Interval,
        configService: ConfigService,
        $http: IHttpService,
        browserTimeFactory: () => Date,
        htmlDomDocumentService: HTMLDomDocumentService,
        fetchOverride: FetchFunction | null = null,
        showToastOverride: ShowToastFunction | null = null,
        promisedTimeoutFactory: (timeout: number) => PromisedTimeout = null,
    ) {
        this._destructor = new Destructor();
        this._timeSyncThreshold = configService.timeSyncThreshold;
        this._$http = $http;
        this._browserTimeFactory = browserTimeFactory;
        this._fetch = fetchOverride ? fetchOverride : fetch;
        this._showToast = showToastOverride ? showToastOverride : showToast;
        this._promisedTimeoutFactory = promisedTimeoutFactory
            ? promisedTimeoutFactory
            : (timeout) => {
                  return new PromisedTimeout(timeout);
              };
        this._configService = configService;

        this._lastTimeToastShown = null;
        this._onChange = new EventRegistry();
        this._interval = undefined;
        this._lastSecondsServerIsAheadOfClient = this._secondsServerIsAheadOfClient;

        this._htmlDomDocumentService = htmlDomDocumentService;
        this._interval = interval;
        this._timeSyncStateMachine = new TimeSyncCheckStateMachine(this);
        // Once logged in, run the check once, then run it on an interval

        this._enterIdleEvent = new EventRegistry();

        this._secondsServerIsAheadOfClient = 0;
        this._serverTimestamp = null;
    }

    // used by ClockHandlerService
    public get secondsServerIsAheadOfClient(): number {
        return this._secondsServerIsAheadOfClient;
    }

    // used by LicensePageStateManager
    public get serverTimestamp(): Date {
        return this._serverTimestamp;
    }

    // subscribers are ClockHandlerService and LicensePageStateManager
    public onChange(fn: () => void): () => void {
        return this._onChange.addEventListener(fn);
    }

    public async destroy(): Promise<void> {
        this._destructor.destroy();
        this._interval.stop();
    }

    private async _requestServerTime(timeoutPromise: Promise<void> = undefined): Promise<Date> {
        return new Date();
        // Expect JSON: {"timestamp": (ISO8601 string)}
        // const response = await this._$http.get(
        //     this._authService.apiUrlRoot + 'current_timestamp/',
        //     {
        //         timeout: timeoutPromise
        //     }
        // );
        //
        // return new Date(response.data.timestamp);
    }

    public checkForOutOfSyncMessage(
        browserTimestamp: Date | null,
        serverTimestamp: Date | null,
        utcTimestamp: Date | null,
    ): TimeSyncError | null {
        // no point continuing if we've got nothing to compare to
        if (!(serverTimestamp || utcTimestamp)) {
            console.warn(`Failed to get any comparison time; cannot check time sync`);
            return null;
        }

        // already shown a toast for the day
        if (
            this._lastTimeToastShown &&
            toUnix(browserTimestamp) - toUnix(this._lastTimeToastShown) < SECONDS_IN_A_DAY
        ) {
            return null;
        }

        // message will be null if there's no time sync error
        return this._getMessage(browserTimestamp, serverTimestamp, utcTimestamp);
    }

    private _getMessage(
        browserTimestamp: Date | null,
        serverTimestamp: Date | null,
        utcTimestamp: Date | null,
    ): TimeSyncError | null {
        const browserUnixSeconds = browserTimestamp ? toUnix(browserTimestamp) : null;
        const serverUnixSeconds = serverTimestamp ? toUnix(serverTimestamp) : null;
        const utcUnixSeconds = utcTimestamp ? toUnix(utcTimestamp) : null;

        let browserAheadOfUTCSeconds = null;
        let browserAheadOfServerSeconds = null;
        let serverAheadOfUTCSeconds = null;

        let needToShowToast = false;
        let isServerTimeError = false;

        if (utcTimestamp) {
            browserAheadOfUTCSeconds = getAheadSeconds(browserUnixSeconds, utcUnixSeconds);
            if (this._exceedsTimeSyncThreshold(browserAheadOfUTCSeconds)) {
                needToShowToast = true;
            }
        }

        if (serverTimestamp) {
            browserAheadOfServerSeconds = getAheadSeconds(browserUnixSeconds, serverUnixSeconds);
            if (this._exceedsTimeSyncThreshold(browserAheadOfServerSeconds)) {
                needToShowToast = true;
            }
        }

        if (serverTimestamp && utcTimestamp) {
            serverAheadOfUTCSeconds = getAheadSeconds(serverUnixSeconds, utcUnixSeconds);
            if (this._exceedsTimeSyncThreshold(serverAheadOfUTCSeconds)) {
                isServerTimeError = true;
                needToShowToast = true;
            }
        }

        if (!needToShowToast) {
            return null;
        }

        let times = ``;
        if (utcTimestamp) {
            times += `${formatDateToISO8601(utcTimestamp)} - Internet<br/>\n`;
        }
        if (serverTimestamp) {
            times += `${formatDateToISO8601(serverTimestamp)} - IMS API<br/>\n`;
        }
        if (browserTimestamp) {
            times += `${formatDateToISO8601(browserTimestamp)} - Browser<br/>\n`;
        }

        let deltas = ``;
        if (this._exceedsTimeSyncThreshold(browserAheadOfUTCSeconds)) {
            deltas += getDelta('Browser', 'Internet', browserAheadOfUTCSeconds);
        }
        if (this._exceedsTimeSyncThreshold(browserAheadOfServerSeconds)) {
            deltas += getDelta('Browser', 'IMS', browserAheadOfServerSeconds);
        }
        if (this._exceedsTimeSyncThreshold(serverAheadOfUTCSeconds)) {
            deltas += getDelta('IMS', 'Internet', serverAheadOfUTCSeconds);
        }

        let msg = `<br/>\n<br/>\n` + times;
        if (deltas.length) {
            msg += `<br/>\n` + deltas;
        }

        if (isServerTimeError) {
            msg += `<br/>\nThe IMS server time is incorrect.<br/>\n<b><a href="https://feedback.ftpsolutions.com.au" target="_blank">Please contact FTP Solutions Support for assistance.</a></b>`;
        } else {
            msg += '<br/><br>\nPlease check the time on your computer is correct.';
        }

        return {
            isCritical: isServerTimeError,
            message: msg,
            title: isServerTimeError
                ? 'Critical time sync error detected!'
                : 'Time sync issues detected.',
        };
    }

    private _exceedsTimeSyncThreshold(aheadSeconds: number | null): boolean {
        if (!aheadSeconds) {
            return false;
        }
        return Math.abs(aheadSeconds) > this._timeSyncThreshold;
    }

    startInterval(): void {
        this._interval.start(
            () => {
                // convert the timeout to ms
                return this._timeSyncStateMachine.handleEvent(TRANS__RUN_INTERVAL);
            },
            this._configService.timeSyncCheckPeriod * 1000,
            false,
        );
    }

    stopInterval(): void {
        this._interval.stop();
    }

    cancelIMSAPITimeRequest(): void {
        log(`Cancel IMS API request`);
        this._requestTimer.cancel();
        this._cancelIMSAPIRequest();
    }

    cancelInternetTimeRequest(): void {
        log(`Cancel internet request`);
        this._requestTimer.cancel();
        this._abortController.abort();
    }

    async compareTimes(): Promise<unknown[]> {
        const browserTimestamp = this._browserTimeFactory();
        // ensure we expose the right values at the old interface
        this._serverTimestamp = this._timeSyncCheckIMSAPITimestamp;

        log(
            `Comparing timestamps: browser ${browserTimestamp}, server ${this._serverTimestamp}, internet ${this._timeSyncCheckInternetTime}`,
        );

        this._secondsServerIsAheadOfClient = getAheadSeconds(
            toUnix(this._serverTimestamp),
            toUnix(browserTimestamp),
        );

        this._lastSecondsServerIsAheadOfClient = this._secondsServerIsAheadOfClient;

        const outOfSyncMessage = this.checkForOutOfSyncMessage(
            browserTimestamp,
            this._serverTimestamp,
            this._timeSyncCheckInternetTime,
        );
        if (outOfSyncMessage) {
            // fire off the toast if we need to and we haven't already
            this._showToast(
                outOfSyncMessage.isCritical,
                outOfSyncMessage.message,
                outOfSyncMessage.title,
            );
            this._lastTimeToastShown = new Date();
        }
        // and ensure we fire the events as expected
        return this._onChange.notifyListeners(
            this._secondsServerIsAheadOfClient,
            this._serverTimestamp,
        );
    }

    async startIMSAPITimeRequest(): Promise<unknown[]> {
        const p = new Promise<void>((resolve) => {
            this._cancelIMSAPIRequest = resolve;
        });
        const startTime = new Date();
        log(`Starting IMS API request`);
        try {
            const d = await this._requestServerTime(p);
            log(`IMS API request done in ${(new Date().getTime() - startTime.getTime()) / 1000.0}`);
            return this._timeSyncStateMachine.handleEvent(TRANS__IMS_API_TIME_REQUEST_SUCCESS, d);
        } catch (e) {
            // This might be OK - depending on network conditions
            console.warn(e);
            log(`IMS API request failed ${e.message}`);
            // Clear the stored timestamp
            return this._timeSyncStateMachine.handleEvent(TRANS__IMS_API_TIME_REQUEST_FAILED);
        }
    }

    async startInternetTimeRequest(): Promise<unknown[]> {
        const startTime = new Date();
        this._abortController = new AbortController();
        log(`Starting internet request`);
        try {
            const d = await requestUTCTime(this._fetch, this._abortController.signal);
            log(
                `Internet request done in ${(new Date().getTime() - startTime.getTime()) / 1000.0}`,
            );
            return this._timeSyncStateMachine.handleEvent(TRANS__INTERNET_TIME_REQUEST_SUCCESS, d);
        } catch (e) {
            // This might be OK - depending on network conditions
            console.warn(e);
            log(`Internet request failed ${e.message}`);
            // Clear the stored timestamp
            return this._timeSyncStateMachine.handleEvent(TRANS__INTERNET_TIME_REQUEST_FAILED);
        }
    }

    async startRequestTimer(): Promise<unknown[]> {
        // convert the timeout to ms
        log('Starting request timer');
        this._requestTimer = this._promisedTimeoutFactory(
            this._configService.timeSyncHttpRequestTimeout * 1000,
        );
        try {
            await this._requestTimer.promise;
            log('Request timeout!');
            return this._timeSyncStateMachine.handleEvent(TRANS__TIMER_TIMEOUT);
        } catch {
            log('Request timer cancelled');
            return null;
        }
    }

    acceptIMSAPITime(d: Date): void {
        log(`storing api time: ${d}`);
        this._timeSyncCheckIMSAPITimestamp = d;
    }

    acceptInternetTime(d: Date): void {
        log(`storing internet time: ${d}`);
        this._timeSyncCheckInternetTime = d;
    }

    addOnEnterIdle(fn: () => void): () => void {
        return this._enterIdleEvent.addEventListener(fn);
    }

    enteringIdle(): Promise<void[]> {
        return this._enterIdleEvent.notifyListeners();
    }
}
